import type { ReservationRescheduleInput } from './reservationTypes'

/**
 * 예약 변경 모달 폼 상태·클라이언트 검증·변환(설계 §2, #669 §1-1). `closureViewModel.ts`와 동일한
 * 명명 관례를 따른다.
 *
 * 🚨 정렬 격자·예약 단위 배수·최대 연속 예약시간 등 서버 전용 규칙은 여기서 재검증하지 않는다 —
 * 최종 판정처는 서버이며, 여기 검증은 왕복을 줄이기 위한 최소한의 사전 검증(필수값·시간 역전·사유
 * 길이·변경 없음)만 담당한다.
 */

export interface RescheduleFormState {
  courtId: number
  /** "yyyy-MM-dd" */
  useDate: string
  /** "HH:mm" */
  startTime: string
  /** "HH:mm" — "24:00" 허용 */
  endTime: string
  reason: string
}

/** 현재 예약값으로 폼을 프리필한다 — 사유는 매번 새로 입력받아야 하므로 빈 값. */
export function initialRescheduleFormState(current: {
  courtId: number
  useDate: string
  startTime: string
  endTime: string
}): RescheduleFormState {
  return {
    courtId: current.courtId,
    useDate: current.useDate,
    startTime: current.startTime,
    endTime: current.endTime,
    reason: '',
  }
}

export interface RescheduleFormErrors {
  courtId?: string
  useDate?: string
  time?: string
  reason?: string
  /** 필드에 속하지 않는 전역 오류(현재는 "변경 사항 없음" 하나). */
  general?: string
}

export function hasRescheduleFormErrors(errors: RescheduleFormErrors): boolean {
  return Object.keys(errors).length > 0
}

const MAX_REASON_LENGTH = 500

/** "HH:mm" → 자정으로부터의 분. "24:00" 은 1440(`closureViewModel.ts`/`operatingHourViewModel.ts` 규칙 승계). */
function toMinutes(time: string): number {
  const [hoursText, minutesText] = time.split(':')
  return Number(hoursText ?? 0) * 60 + Number(minutesText ?? 0)
}

/** 코트·날짜·시작·종료가 현재 예약값과 전부 같은가 — 서버 400("변경 없음")을 클라이언트가 먼저 막는다. */
function isUnchanged(
  form: RescheduleFormState,
  current: { courtId: number; useDate: string; startTime: string; endTime: string }
): boolean {
  return (
    form.courtId === current.courtId &&
    form.useDate === current.useDate &&
    form.startTime === current.startTime &&
    form.endTime === current.endTime
  )
}

export function validateRescheduleForm(
  form: RescheduleFormState,
  current: { courtId: number; useDate: string; startTime: string; endTime: string }
): RescheduleFormErrors {
  const errors: RescheduleFormErrors = {}

  if (!form.courtId) errors.courtId = '코트를 선택해 주세요.'
  if (form.useDate.length === 0) errors.useDate = '날짜를 선택해 주세요.'

  if (form.startTime.length === 0 || form.endTime.length === 0) {
    errors.time = '시작·종료 시각을 선택해 주세요.'
  } else if (toMinutes(form.endTime) <= toMinutes(form.startTime)) {
    errors.time = '종료 시각은 시작 시각보다 뒤여야 합니다.'
  }

  const trimmedReason = form.reason.trim()
  if (trimmedReason.length === 0) {
    errors.reason = '변경 사유를 입력해 주세요.'
  } else if (trimmedReason.length > MAX_REASON_LENGTH) {
    errors.reason = `사유는 최대 ${MAX_REASON_LENGTH}자까지 입력할 수 있습니다.`
  }

  if (!hasRescheduleFormErrors(errors) && isUnchanged(form, current)) {
    errors.general = '변경된 내용이 없습니다. 코트·날짜·시간 중 하나 이상을 바꿔 주세요.'
  }

  return errors
}

export function toRescheduleInput(form: RescheduleFormState): ReservationRescheduleInput {
  return {
    courtId: form.courtId,
    useDate: form.useDate,
    startTime: form.startTime,
    endTime: form.endTime,
    reason: form.reason.trim(),
  }
}
